<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="items"></PageHeader>
    <div class="row">
      <!-- Create — behind the "Add community" button, so the list gets the full page -->
      <b-modal
        v-model="showCreate"
        title="Add a community"
        size="lg"
        hide-footer
      >
        <div>
            <b-form-group label="Name">
              <b-form-input
                v-model="communityForm.name"
                placeholder="e.g. Cardiology India"
                maxlength="120"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Description">
              <b-form-textarea
                v-model="communityForm.description"
                rows="2"
                max-rows="4"
                maxlength="500"
                placeholder="What is this community for?"
              ></b-form-textarea>
            </b-form-group>
            <b-form-group label="Logo">
              <div class="d-flex align-items-center">
                <img
                  v-if="communityForm.avatar"
                  :src="communityForm.avatar"
                  class="logo-preview mr-3"
                />
                <div v-else class="logo-preview logo-placeholder mr-3">
                  {{ initialsOf(communityForm.name) }}
                </div>
                <div>
                  <label class="btn btn-sm btn-light mb-1">
                    {{ uploadingLogo ? "Uploading…" : "Choose image" }}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      hidden
                      :disabled="uploadingLogo"
                      @change="onPickLogo"
                    />
                  </label>
                  <button
                    v-if="communityForm.avatar"
                    class="btn btn-sm btn-link text-danger d-block p-0"
                    @click="communityForm.avatar = ''"
                  >
                    Remove
                  </button>
                  <div class="small text-muted">
                    Square image works best. Leave empty to auto-generate from
                    the name.
                  </div>
                </div>
              </div>
            </b-form-group>
            <b-form-group label="Visibility">
              <b-form-select
                v-model="communityForm.visibility"
                :options="[
                  { value: 'public', text: 'Public — listed in the app directory' },
                  { value: 'private', text: 'Private — not listed' },
                ]"
              ></b-form-select>
            </b-form-group>
            <b-form-group label="Joining">
              <b-form-select
                v-model="communityForm.joinPolicy"
                :options="[
                  { value: 'approval', text: 'Approval required' },
                  { value: 'open', text: 'Open — join instantly' },
                ]"
              ></b-form-select>
            </b-form-group>
            <div class="d-flex">
              <button class="btn btn-light mr-2" @click="showCreate = false">
                Cancel
              </button>
              <button
                class="btn btn-primary flex-grow-1"
                :disabled="!communityForm.name.trim() || savingCommunity"
                @click="submitCommunity"
              >
                {{ savingCommunity ? "Creating…" : "Create community" }}
              </button>
            </div>
        </div>
      </b-modal>

      <!-- List -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center mb-3">
              <h5 class="mb-0 mr-3">Communities</h5>
              <b-form-input
                v-model="communityFilter"
                size="sm"
                style="max-width: 260px"
                class="mr-2"
                placeholder="Search name, state…"
              ></b-form-input>
              <b-form-select
                v-model="communityTypeFilter"
                size="sm"
                style="max-width: 210px"
                class="mr-2"
                :options="[
                  { value: 'all', text: 'All communities' },
                  { value: 'custom', text: 'Created by admin' },
                  { value: 'derived', text: 'Speciality × state' },
                ]"
              ></b-form-select>
              <span class="text-muted small mr-auto">
                {{ showingRange(filteredCommunities, communityPage) }}
              </span>
              <button class="btn btn-sm btn-light mr-2" @click="fetchCommunities">
                <i class="mdi mdi-refresh"></i> Refresh
              </button>
              <button
                v-if="can('manage-communities')"
                class="btn btn-sm btn-primary"
                @click="openCreate"
              >
                <i class="mdi mdi-plus"></i> Add community
              </button>
            </div>

            <b-table
              :items="pagedCommunities"
              :fields="communityFields"
              responsive
              show-empty
              :empty-text="
                communityFilter || communityTypeFilter !== 'all'
                  ? 'No communities match this search.'
                  : 'No communities created yet.'
              "
              class="mb-0"
            >
              <template v-slot:cell(name)="row">
                <div class="d-flex align-items-center">
                  <img :src="row.item.avatar" class="row-logo mr-2" />
                  <div>
                    <div class="font-weight-bold">
                      {{ row.item.name }}
                      <span
                        v-if="row.item.type !== 'custom'"
                        class="badge badge-light border ml-1"
                        title="Membership comes from the doctor's profile — nobody joins these"
                      >
                        auto
                      </span>
                    </div>
                    <div class="small text-muted">{{ row.item.description }}</div>
                  </div>
                </div>
              </template>
              <template v-slot:cell(joinPolicy)="row">
                <span
                  class="badge"
                  :class="row.item.joinPolicy === 'open' ? 'badge-success' : 'badge-warning'"
                >
                  {{ row.item.joinPolicy === "open" ? "Open" : "Approval" }}
                </span>
              </template>
              <template v-slot:cell(visibility)="row">
                {{ row.item.visibility === "public" ? "Public" : "Private" }}
              </template>
              <template v-slot:cell(pendingCount)="row">
                <span
                  v-if="row.item.pendingCount"
                  class="badge badge-danger"
                >{{ row.item.pendingCount }}</span>
                <span v-else class="text-muted">0</span>
              </template>
              <template v-slot:cell(isActive)="row">
                <span
                  class="badge"
                  :class="row.item.isActive ? 'badge-success' : 'badge-secondary'"
                >{{ row.item.isActive ? "Active" : "Inactive" }}</span>
              </template>
              <template v-slot:cell(action)="row">
                <button
                  v-if="can('approve-community-requests') && row.item.type === 'custom'"
                  class="btn btn-sm btn-outline-primary mr-1"
                  @click="openRequests(row.item)"
                >
                  Requests
                </button>
                <router-link
                  class="btn btn-sm btn-light mr-1"
                  :to="{
                    name: 'community-chat-thread',
                    params: { id: row.item.code },
                    query: { name: row.item.name },
                  }"
                >
                  Chat
                </router-link>
                <label
                  v-if="can('manage-communities')"
                  class="btn btn-sm btn-light mr-1 mb-0"
                  title="Change logo"
                >
                  Logo
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    :disabled="uploadingLogo"
                    @change="onChangeLogo(row.item, $event)"
                  />
                </label>
                <button
                  v-if="can('manage-communities')"
                  class="btn btn-sm btn-light"
                  @click="toggleActive(row.item)"
                >
                  {{ row.item.isActive ? "Deactivate" : "Activate" }}
                </button>
              </template>
            </b-table>

            <div
              v-if="filteredCommunities.length > perPage"
              class="d-flex flex-wrap align-items-center mt-3"
            >
              <b-form-select
                v-model="perPage"
                size="sm"
                style="max-width: 110px"
                :options="perPageOptions.map((n) => ({ value: n, text: n + ' / page' }))"
              ></b-form-select>
              <span class="text-muted small ml-2 mr-auto">
                {{ showingRange(filteredCommunities, communityPage) }}
              </span>
              <b-pagination
                v-model="communityPage"
                :total-rows="filteredCommunities.length"
                :per-page="perPage"
                size="sm"
                class="mb-0"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Join requests -->
    <b-modal
      v-model="showRequests"
      :title="activeCommunity ? activeCommunity.name + ' — join requests' : 'Join requests'"
      size="lg"
      hide-footer
    >
      <div class="d-flex mb-3">
        <b-form-select
          v-model="requestStatus"
          style="max-width: 220px"
          :options="[
            { value: 'pending', text: 'Pending' },
            { value: 'approved', text: 'Approved' },
            { value: 'rejected', text: 'Rejected' },
          ]"
          @change="fetchRequests(activeCommunity.code, requestStatus)"
        ></b-form-select>
        <span class="ml-auto align-self-center text-muted small">
          {{ requestsTotal }} total
        </span>
      </div>

      <p v-if="!requests.length" class="text-center text-muted my-4">
        Nothing here.
      </p>

      <div
        v-for="r in requests"
        :key="r.memberId"
        class="d-flex align-items-center justify-content-between border-bottom py-2"
      >
        <div>
          <div class="font-weight-bold">
            {{ r.name || "Member " + r.memberId }}
          </div>
          <div class="small text-muted">
            id {{ r.memberId }} · requested {{ formatTime(r.requestedAt) }}
          </div>
        </div>
        <div v-if="r.status === 'pending'">
          <button
            class="btn btn-sm btn-success mr-1"
            :disabled="deciding === r.memberId"
            @click="decide(r.memberId, true)"
          >
            Approve
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            :disabled="deciding === r.memberId"
            @click="decide(r.memberId, false)"
          >
            Reject
          </button>
        </div>
        <span
          v-else
          class="badge"
          :class="r.status === 'approved' ? 'badge-success' : 'badge-secondary'"
        >{{ r.status }}</span>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import MixinRequest from "@/mixins/request";
import communityChatMixin from "@/mixins/ModuleJs/community-chat";

export default {
  data() {
    return {
      pageTitle: "Discover communities",
      items: [
        { text: "Home", href: "/" },
        { text: "Community Chat", href: "/community-chat" },
        { text: "Discover communities" },
      ],
      showRequests: false,
      showCreate: false,
    };
  },
  mixins: [communityChatMixin, MixinRequest],
  components: {
    Layout,
    PageHeader,
  },
  async created() {
    await this.fetchCommunities();
  },
  methods: {
    initialsOf(name) {
      return (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0))
        .join("")
        .toUpperCase();
    },
    openCreate() {
      // Fresh form every time — a cancelled attempt shouldn't linger.
      this.communityForm = {
        name: "",
        description: "",
        avatar: "",
        visibility: "public",
        joinPolicy: "approval",
      };
      this.showCreate = true;
    },
    // createCommunity (mixin) only clears the form on success, so close on that.
    async submitCommunity() {
      await this.createCommunity();
      if (!this.communityForm.name) this.showCreate = false;
    },
    async openRequests(community) {
      this.activeCommunity = community;
      this.showRequests = true;
      await this.fetchRequests(community.code, "pending");
    },
    // Approving changes member/pending counts, so refresh the list behind the modal.
    async decide(memberId, approve) {
      await this.decideRequest(this.activeCommunity.code, memberId, approve);
      await this.fetchCommunities();
    },
  },
};
</script>

<style scoped>
.logo-preview {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
}
.logo-placeholder {
  background: #3d50c4;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.row-logo {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
}
</style>
